/**
 * MobileProductFullScreen — tier 2 of the mobile Products two-tier browse: a
 * full-screen push (slide-in) matching the Trioty product detail. It mirrors
 * MobileOrderFullScreen for the outer shell, Web-Animations slide-in, close and
 * safe-area handling.
 *
 * This is a read-oriented VIEW (not an inline editor): it shows the product's
 * hero, price, inventory, variants and description, with an Edit affordance
 * (header pencil + bottom-bar primary) that navigates to the existing product
 * editor. Every real action — Share, Show/Hide, Edit stock, Delete — reuses the
 * shared useProductCardActions hook threaded from ProductsPage, so no behaviour
 * is reimplemented and the page's dialogs (delete confirm, stock editor) drive
 * the flow.
 *
 * DATA HONESTY: the product API carries no analytics (no sold/views/revenue) and
 * no SKU/barcode/sales-channel fields, so the "stats" row and the Sales-channels
 * section from the visual design are omitted rather than faked. Status lives in
 * the hero badge; stock and variant counts live in their own cards, which is why
 * a separate 3-card stats strip would only be redundant.
 */

import { useState } from "react";
import { createPortal } from "react-dom";
import { useSlideScreen } from "@/shared/hooks/useSlideScreen";
import { LoadingSkeleton } from "@a2z/ui";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { imageUrl } from "@a2z/ui/media/image-url";
import { MEDIA_BASE_URL } from "@/lib/media-base";
import { ErrorState } from "@/shared/components/ErrorState";
import { ShareMenu } from "@/shared/components/ShareMenu";
import { ProductCheckoutLinkSheet } from "@/modules/products/components/ProductCheckoutLinkSheet";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { pickLocalized } from "@/lib/i18n/localized";
import { formatCurrency } from "@/lib/formatting/currency";
import { getBuyerAbsoluteUrl } from "@/lib/routing/portal-urls";
import type { Locale } from "@/lib/i18n/config";
import type { ProductView } from "@/lib/api/types";
import { useProductByIdQuery } from "@/modules/products/services/useProductByIdQuery";
import { useCategoriesQuery } from "@/shared/services/useCategoriesQuery";
import { useStoreQuery } from "@/shared/services/useStoreQuery";
import {
  LOW_STOCK_THRESHOLD,
  deriveProductStatus,
  getPurchasableStockTotal,
  hasUnlimitedStock,
  isPurchasableVariant,
  type ProductCardStatus,
} from "@/modules/products/lib/cardAttention";
import type { ProductCardActions } from "@/modules/products/lib/useProductCardActions";

type Props = {
  storeSlug: string;
  productId: string | null;
  /** Products route base — Edit navigates to `${basePath}/${productId}`. */
  basePath: string;
  locale: Locale;
  onClose: () => void;
  /** Shared inline actions (visibility · stock · delete), owned by ProductsPage. */
  actions: ProductCardActions;
};

const STATUS_META: Record<ProductCardStatus, { labelKey: string; color: string }> = {
  active: { labelKey: keys.products.editSidebarInventoryInStock, color: "var(--success-500)" },
  low_stock: { labelKey: keys.products.statusLowStock, color: "var(--warning-500)" },
  out_of_stock: { labelKey: keys.products.statusOutOfStock, color: "var(--error-500)" },
  hidden: { labelKey: keys.products.editSidebarPublishingHidden, color: "var(--text-disabled)" },
};

function parseMoney(value: string | null): number | null {
  if (typeof value !== "string") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getHeroImage(product: ProductView): string | null {
  const readyImage = product.images.find((img) => img.status === "READY");
  if (!readyImage?.objectKey) return null;
  return imageUrl(
    readyImage.objectKey,
    { width: 640, format: "auto", removeBackground: readyImage.useBgRemoved === true },
    { baseUrl: MEDIA_BASE_URL },
  );
}

export function MobileProductFullScreen({
  storeSlug,
  productId,
  basePath,
  locale,
  onClose,
  actions,
}: Props) {
  // Mount only while open; a fresh mount per product id replays the slide-in.
  if (!productId) return null;
  return (
    <Screen
      key={productId}
      storeSlug={storeSlug}
      productId={productId}
      basePath={basePath}
      locale={locale}
      onClose={onClose}
      actions={actions}
    />
  );
}

function Screen({
  storeSlug,
  productId,
  basePath,
  locale,
  onClose,
  actions,
}: {
  storeSlug: string;
  productId: string;
  basePath: string;
  locale: Locale;
  onClose: () => void;
  actions: ProductCardActions;
}) {
  // Slide in on open; the back button (via `close`) plays the reverse slide-out
  // before calling onClose. When we arrive here as the "back" target from the
  // editor, skip the enter animation so only the editor's slide-out plays — one
  // motion, not a jarring close-then-open.
  const cameFromEdit = (useLocation().state as { fromEdit?: boolean } | null)?.fromEdit === true;
  const { ref, close } = useSlideScreen<HTMLDivElement>(onClose, {
    skipEnter: cameFromEdit,
  });

  // Portal to <body>: rendered inline, this `fixed` overlay would sit inside the
  // AppShell <main> (an overflow-y-auto scroll container), where iOS Safari
  // couples its scroll to the parent and overscroll chains into the page behind.
  return createPortal(
    <div
      ref={ref}
      className="fixed inset-0 z-[70] flex flex-col bg-[color:var(--canvas)] lg:hidden"
    >
      <Panel
        storeSlug={storeSlug}
        productId={productId}
        basePath={basePath}
        locale={locale}
        onClose={close}
        actions={actions}
      />
    </div>,
    document.body,
  );
}

function Panel({
  storeSlug,
  productId,
  basePath,
  locale,
  onClose,
  actions,
}: {
  storeSlug: string;
  productId: string;
  basePath: string;
  locale: Locale;
  onClose: () => void;
  actions: ProductCardActions;
}) {
  const t = useTranslations();
  const { data: product, isLoading, error } = useProductByIdQuery({
    storeSlug,
    productId,
  });

  if (error && !isLoading) {
    return (
      <>
        <TopBar onClose={onClose} right={null} />
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <ErrorState message={t(keys.errors.generic)} />
        </div>
      </>
    );
  }
  if (isLoading || !product) {
    return (
      <>
        <TopBar onClose={onClose} right={null} />
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <LoadingSkeleton variant="card" rows={6} />
        </div>
      </>
    );
  }
  return (
    <LoadedScreen
      product={product}
      storeSlug={storeSlug}
      basePath={basePath}
      locale={locale}
      onClose={onClose}
      actions={actions}
    />
  );
}

/** Top-bar chrome: Back · right-hand actions cluster. Shared across states. */
function TopBar({ onClose, right }: { onClose: () => void; right: React.ReactNode }) {
  const t = useTranslations();
  return (
    <div
      className="flex shrink-0 items-center justify-between gap-2 px-3 pb-3"
      style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t(keys.common.back)}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
      >
        <BackIcon />
      </button>
      <div className="flex items-center gap-1.5">{right}</div>
    </div>
  );
}

function LoadedScreen({
  product,
  storeSlug,
  basePath,
  locale,
  onClose,
  actions,
}: {
  product: ProductView;
  storeSlug: string;
  basePath: string;
  locale: Locale;
  onClose: () => void;
  actions: ProductCardActions;
}) {
  const t = useTranslations();
  const navigate = useNavigate();
  const [shareOpen, setShareOpen] = useState(false);

  const { data: categoriesData } = useCategoriesQuery({ storeSlug });
  const categories = categoriesData ?? [];
  const { data: storeData } = useStoreQuery({ storeSlug });
  const storeLowStock = storeData?.lowStockThreshold;

  const name = pickLocalized(locale, product.nameAr, product.nameEn);
  const status = deriveProductStatus(product, storeLowStock);
  const heroImage = getHeroImage(product);

  const primaryCategoryName =
    product.categoryIds
      .map((id) => categories.find((c) => c.id === id))
      .filter(Boolean)
      .map((c) => pickLocalized(locale, c!.nameAr, c!.nameEn))[0] ?? null;

  // Pricing — mirror the list-row derivation.
  const defaultVariant =
    product.variants.find((v) => v.id === product.defaultVariantId) ??
    product.variants.find((v) => v.isDefault) ??
    product.variants[0];
  const fallbackCurrent = defaultVariant
    ? (defaultVariant.discountedPrice ?? defaultVariant.price)
    : 0;
  const fallbackOriginal = defaultVariant ? defaultVariant.price : null;
  const currentMin = parseMoney(product.pricingSummary.currentMin) ?? fallbackCurrent;
  const originalMin = parseMoney(product.pricingSummary.originalMin) ?? fallbackOriginal;
  const hasDiscount = originalMin !== null && originalMin > currentMin;
  const discountPct =
    hasDiscount && originalMin ? Math.round((1 - currentMin / originalMin) * 100) : 0;

  const stockTotal = getPurchasableStockTotal(product);
  const lowStockThreshold =
    product.lowStockThreshold ?? storeLowStock ?? LOW_STOCK_THRESHOLD;

  const hasVariantOptions = product.optionGroups.length > 0;

  const description = pickLocalized(
    locale,
    product.shortDescriptionAr ?? "",
    product.shortDescriptionEn,
  );

  const editHref = `${basePath}/${product.id}${product.images.length === 0 ? "#photos" : ""}`;
  const goEdit = () => navigate(editHref);

  const shareUrl = getBuyerAbsoluteUrl(
    locale,
    storeSlug,
    `/products/${encodeURIComponent(product.productRef)}`,
  );

  const actionTarget = { id: product.id, name, isActive: product.isActive };
  const menuItemBase =
    "flex w-full items-center gap-3 rounded-[10px] px-2.5 py-2.5 text-start text-[14px] font-medium data-[focus]:bg-[color:var(--soft)]";

  const stockColor = STATUS_META[status].color;

  return (
    <>
      <TopBar
        onClose={onClose}
        right={
          <Menu as="div" className="relative flex">
            <MenuButton
              aria-label={t(keys.orders.mMore)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
            >
              <DotsIcon />
            </MenuButton>
            <MenuItems
              anchor="bottom end"
              className="z-[80] mt-1 w-[190px] rounded-[14px] border border-[color:var(--card-br)] bg-[color:var(--card)] p-1.5 shadow-[0_20px_54px_rgba(0,0,0,0.28)] focus:outline-none"
            >
              <MenuItem>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    actions.requestDelete(actionTarget);
                  }}
                  className={`${menuItemBase} text-[color:var(--error-500)]`}
                >
                  <span><TrashIcon /></span>
                  {t(keys.common.delete)}
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        }
      />

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-[118px] pt-1">
        {/* Hero */}
        <div className="relative h-[206px] overflow-hidden rounded-[22px] border border-[color:var(--hair)]">
          {heroImage ? (
            <img src={heroImage} alt="" aria-hidden="true" className="h-full w-full object-cover" />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center text-[64px] font-extrabold text-[color:var(--text-secondary)]"
              style={{
                background:
                  "color-mix(in srgb, var(--accent-500) 8%, var(--soft2))",
              }}
            >
              {name.trim().charAt(0).toUpperCase() || "•"}
            </div>
          )}
          <div
            className="absolute start-3 top-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold backdrop-blur"
            style={{
              background: "color-mix(in srgb, var(--canvas) 72%, transparent)",
              color: "var(--text-primary)",
            }}
          >
            <span
              aria-hidden="true"
              className="h-2 w-2 rounded-full"
              style={{ background: STATUS_META[status].color }}
            />
            {t(STATUS_META[status].labelKey)}
          </div>
        </div>

        {/* Category chip */}
        {primaryCategoryName ? (
          <div className="mt-4">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold"
              style={{
                background: "color-mix(in srgb, var(--accent-500) 10%, transparent)",
                color: "var(--accent-500)",
              }}
            >
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: "var(--accent-500)" }}
              />
              {primaryCategoryName}
            </span>
          </div>
        ) : null}

        {/* Name */}
        <h1 className="mt-3 text-[24px] font-extrabold leading-tight tracking-[-0.02em] text-[color:var(--text-primary)]">
          {name}
        </h1>

        {/* Price row */}
        <div className="mt-2 flex flex-wrap items-baseline gap-2.5">
          <span className="text-[26px] font-extrabold tracking-[-0.01em] text-[color:var(--text-primary)]">
            <bdi>{formatCurrency(currentMin, locale)}</bdi>
          </span>
          {hasDiscount && originalMin ? (
            <span className="text-[15px] text-[color:var(--text-muted)] line-through">
              <bdi>{formatCurrency(originalMin, locale)}</bdi>
            </span>
          ) : null}
          {hasDiscount && discountPct > 0 ? (
            <span
              className="rounded-[7px] px-2 py-0.5 text-[12px] font-extrabold"
              style={{
                background: "color-mix(in srgb, var(--warning-500) 15%, transparent)",
                color: "var(--warning-500)",
              }}
            >
              {t(keys.products.mDiscountShort, { pct: discountPct })}
            </span>
          ) : null}
        </div>

        {/* Inventory */}
        <SectionLabel>{t(keys.products.editSidebarInventory)}</SectionLabel>
        <InfoCard>
          <InfoRow label={t(keys.products.mDetailStockOnHand)}>
            <span style={{ color: stockColor }} className="font-bold">
              {hasUnlimitedStock(product)
                ? t(keys.products.editSidebarInventoryUnlimited)
                : stockTotal === 0
                  ? t(keys.products.mDetailStockNone)
                  : t(keys.products.mDetailUnitsCount, { count: stockTotal })}
            </span>
          </InfoRow>
          <InfoRow label={t(keys.products.editSidebarInventoryLowStockAlert)}>
            {t(keys.products.mDetailLowStockBelow, { count: lowStockThreshold })}
          </InfoRow>
          <InfoRow label={t(keys.products.mDetailVisibility)} last>
            {product.isActive
              ? t(keys.products.editSidebarPublishingPublished)
              : t(keys.products.editSidebarPublishingHidden)}
          </InfoRow>
        </InfoCard>

        {/* Variants */}
        {hasVariantOptions ? (
          <>
            <SectionLabel
              trailing={t(keys.products.stockOptionsCount, { count: product.variants.length })}
            >
              {t(keys.products.variantsCardTitle)}
            </SectionLabel>
            <InfoCard>
              {product.variants.map((v, i) => {
                const purchasable = isPurchasableVariant(v);
                const dotColor = !purchasable
                  ? "var(--error-500)"
                  : v.stockCount <= 3
                    ? "var(--warning-500)"
                    : "var(--success-500)";
                const vName =
                  v.selections
                    .map((s) => pickLocalized(locale, s.optionValueAr, s.optionValueEn))
                    .filter(Boolean)
                    .join(" / ") || name;
                const vPrice = v.discountedPrice ?? v.price;
                return (
                  <div
                    key={v.id}
                    className={`flex items-center gap-3 py-3 ${
                      i === product.variants.length - 1
                        ? ""
                        : "border-b border-[color:var(--hair)]"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: dotColor }}
                    />
                    <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-[color:var(--text-primary)]">
                      {vName}
                    </span>
                    <span className="shrink-0 text-[12.5px] text-[color:var(--text-muted)]">
                      {purchasable
                        ? t(keys.products.mStockLeft, { count: v.stockCount })
                        : t(keys.products.statusOutOfStock)}
                    </span>
                    <span className="shrink-0 text-[13.5px] font-bold text-[color:var(--text-primary)]">
                      <bdi>{formatCurrency(vPrice, locale)}</bdi>
                    </span>
                  </div>
                );
              })}
            </InfoCard>
          </>
        ) : null}

        {/* Description */}
        {description.trim() ? (
          <>
            <SectionLabel>{t(keys.products.mDetailDescription)}</SectionLabel>
            <p className="whitespace-pre-line text-[14px] leading-relaxed text-[color:var(--text-primary)]">
              {description}
            </p>
          </>
        ) : null}

        {/* Quick actions (bottom, like the design) — a publish toggle + an
            edit-stock row. Edit stock closes the view first so its sheet (a
            default-layer ModalSheet) isn't hidden behind this z-[70] overlay. */}
        <div className="mt-6 overflow-hidden rounded-[16px] border border-[color:var(--hair)] bg-[color:var(--soft)]">
          <div className="flex items-center justify-between gap-3 px-4 py-3.5">
            <div className="min-w-0">
              <div className="text-[14px] font-semibold text-[color:var(--text-primary)]">
                {t(keys.products.editSidebarPublishingVisible)}
              </div>
              <div className="mt-0.5 flex items-center gap-1.5 text-[11.5px] font-medium text-[color:var(--text-muted)]">
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 rounded-full"
                  style={{
                    background: product.isActive
                      ? "var(--success-500)"
                      : "var(--text-disabled)",
                  }}
                />
                {product.isActive
                  ? t(keys.products.editSidebarPublishingPublished)
                  : t(keys.products.editSidebarPublishingHidden)}
              </div>
            </div>
            <Toggle
              on={product.isActive}
              disabled={actions.pendingVisibilityId === product.id}
              ariaLabel={t(keys.products.editSidebarPublishingVisible)}
              onToggle={() => void actions.toggleVisibility(actionTarget)}
            />
          </div>
          <div className="h-px bg-[color:var(--hair)]" aria-hidden="true" />
          <button
            type="button"
            onClick={() => {
              onClose();
              actions.requestEditStock(product.id);
            }}
            className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-start transition-colors hover:bg-[color:var(--soft2)]"
          >
            <span className="flex items-center gap-2.5 text-[14px] font-semibold text-[color:var(--text-primary)]">
              <span className="text-[color:var(--text-muted)]"><BoxIcon /></span>
              {t(keys.products.variantsEditStock)}
            </span>
            <ChevronRightIcon />
          </button>
          <div className="h-px bg-[color:var(--hair)]" aria-hidden="true" />
          <ProductCheckoutLinkSheet
            storeSlug={storeSlug}
            productId={product.id}
            variants={product.variants}
            elevated
            triggerClassName="flex w-full items-center gap-2.5 px-4 py-3.5 text-start text-[14px] font-semibold text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--soft2)] [&>svg]:h-4 [&>svg]:w-4 [&>svg]:text-[color:var(--text-muted)]"
          />
        </div>
      </div>

      {/* Bottom action bar */}
      <div
        className="absolute bottom-0 start-0 end-0 flex items-center gap-2.5 px-5 pt-8"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 14px)",
          background:
            "linear-gradient(to top, var(--canvas) 55%, color-mix(in srgb, var(--canvas) 0%, transparent))",
        }}
      >
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          aria-label={t(keys.products.shareAria)}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[15px] border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
        >
          <ShareIcon />
        </button>
        <button
          type="button"
          onClick={goEdit}
          className="flex h-12 flex-1 items-center justify-center gap-2 rounded-[15px] bg-[color:var(--accent-500)] px-4 text-[15px] font-semibold text-[color:var(--on-accent)] transition-opacity hover:opacity-90"
        >
          <EditIcon />
          {t(keys.products.mDetailEditProduct)}
        </button>
      </div>

      {/* Controlled share sheet — opened from the header menu and the bottom bar. */}
      <ShareMenu
        elevated
        url={shareUrl}
        messageKey={keys.products.shareMessage}
        messageValuesEn={{ name: product.nameEn ?? product.nameAr }}
        messageValuesAr={{ name: product.nameAr }}
        title={t(keys.products.shareTitle)}
        triggerAriaLabel={t(keys.products.shareAria)}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
      />
    </>
  );
}

// ─── Presentational bits ─────────────────────────────────────────────────────

function SectionLabel({
  children,
  trailing,
}: {
  children: React.ReactNode;
  trailing?: string;
}) {
  return (
    <div className="mb-2 mt-6 flex items-center justify-between">
      <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[color:var(--text-muted)]">
        {children}
      </span>
      {trailing ? (
        <span className="text-[12px] font-semibold text-[color:var(--text-muted)]">
          {trailing}
        </span>
      ) : null}
    </div>
  );
}

function InfoCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[16px] border border-[color:var(--hair)] bg-[color:var(--soft)] px-4">
      {children}
    </div>
  );
}

function InfoRow({
  label,
  children,
  last,
}: {
  label: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-3 py-3 ${
        last ? "" : "border-b border-[color:var(--hair)]"
      }`}
    >
      <span className="text-[13.5px] text-[color:var(--text-muted)]">{label}</span>
      <span className="text-end text-[13.5px] font-semibold text-[color:var(--text-primary)]">
        {children}
      </span>
    </div>
  );
}

// ─── Icons ───────────────────────────────────────────────────────────────────

const ICON = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.9,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function BackIcon() {
  return (
    <svg {...ICON} width={18} height={18} strokeWidth={2} className="rtl:-scale-x-100">
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}
function EditIcon() {
  return (
    <svg {...ICON} width={16} height={16}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}
function DotsIcon() {
  return (
    <svg {...ICON} width={16} height={16} stroke="none" fill="currentColor">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}
function ShareIcon() {
  return (
    <svg {...ICON} width={17} height={17}>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
    </svg>
  );
}
function BoxIcon() {
  return (
    <svg {...ICON} width={16} height={16}>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="M3.27 6.96 12 12.01l8.73-5.05" />
      <path d="M12 22.08V12" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg {...ICON} width={16} height={16}>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}
function ChevronRightIcon() {
  return (
    <svg {...ICON} width={16} height={16} className="rtl:-scale-x-100 text-[color:var(--text-disabled)]">
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

/** Design toggle switch (40×24 track, sliding white knob). RTL-aware. */
function Toggle({
  on,
  disabled,
  ariaLabel,
  onToggle,
}: {
  on: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onToggle}
      className="relative h-6 w-10 shrink-0 rounded-full transition-colors disabled:opacity-50"
      style={{ background: on ? "var(--accent-500)" : "var(--border-strong)" }}
    >
      <span
        aria-hidden="true"
        className="absolute top-0.5 h-5 w-5 rounded-full shadow-[0_2px_5px_rgba(0,0,0,0.35)] transition-all"
        style={{ insetInlineStart: on ? "18px" : "2px", background: on ? "var(--on-accent)" : "#fff" }}
      />
    </button>
  );
}
